import { db, functions } from '../firebase';
import { collection, getDocs, doc, getDoc, query, orderBy, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { isDevPreview } from './devPreview';

// Statuses that should be visible to the public on the /summer pages.
const PUBLIC_STATUSES = ['open', 'closed'];

function isPubliclyVisible(workshop, { includeDrafts = false } = {}) {
	// Locally (isDevPreview) show drafts/unpublished too so the flows are testable;
	// in production isDevPreview() is false, so the public filter is unchanged.
	if (includeDrafts || isDevPreview()) return workshop.status !== 'archived';
	return PUBLIC_STATUSES.includes(workshop.status);
}

// Fetch the workshop catalog for the public landing page. Drafts are hidden
// unless includeDrafts is set (admin preview / local dev). The tightened rules
// (Review 3 #1) only allow anonymous reads of open/closed docs, so the public
// path MUST constrain the query to those statuses — an unfiltered query that
// could return a draft is denied wholesale (rules are not filters). The admin
// path keeps the unfiltered orderBy query (isAdmin()/isEmployeeOrAdmin() in the
// rules allows all statuses). Sort client-side so both paths return the same
// order without needing a status+sortOrder composite index.
export async function fetchWorkshops({ includeDrafts = false } = {}) {
	const base = collection(db, 'workshops');
	const snap = includeDrafts
		? await getDocs(query(base, orderBy('sortOrder')))
		: await getDocs(query(base, where('status', 'in', PUBLIC_STATUSES)));
	return snap.docs
		.map((d) => ({ id: d.id, ...d.data() }))
		.filter((w) => isPubliclyVisible(w, { includeDrafts }))
		.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

// Fetch a single workshop by its slug (doc id === slug). Returns null if missing.
export async function fetchWorkshopBySlug(slug) {
	if (!slug) return null;
	const ref = doc(db, 'workshops', slug);
	const docSnap = await getDoc(ref);
	if (!docSnap.exists()) return null;
	return { id: docSnap.id, ...docSnap.data() };
}

// Total remaining seats across all groups (capacity - enrolled - held).
export function workshopSeatsRemaining(workshop) {
	if (!workshop?.groups?.length) return 0;
	return workshop.groups.reduce((sum, g) => {
		const remaining = (g.capacity || 0) - (g.enrolledCount || 0) - (g.heldCount || 0);
		return sum + Math.max(0, remaining);
	}, 0);
}

// Seats remaining for a single group.
export function groupSeatsRemaining(group) {
	if (!group) return 0;
	return Math.max(0, (group.capacity || 0) - (group.enrolledCount || 0) - (group.heldCount || 0));
}

// Compose a session radio's human label from STRUCTURED fields so a fact is
// never printed twice. The catalog stores `label` as the group identity only
// ("Group A"); the age range and time live in their own fields. This is the
// single place that assembles "Group A · Ages 8–11 (9:00 AM–11:00 AM)", used by
// both the registration and waitlist radios. (Earlier the time was baked into
// `label` AND appended here → the duplicate "(4:00 PM–6:00 PM)" bug.)
export function formatSessionOption(group) {
	if (!group) return '';
	const ages = group.ageRange ? ` · Ages ${group.ageRange}` : '';
	const time =
		group.startTime && group.endTime ? ` (${group.startTime}–${group.endTime})` : '';
	return `${group.label || ''}${ages}${time}`;
}

// Validate a student's age against a group's age range (e.g. "8–11"). Returns
// an error message if out of range, or '' when fine / no restriction. Mirrors
// the server-side check in functions (which is the source of truth).
export function ageRangeError(group, age) {
	if (!group || !group.ageRange) return '';
	const m = String(group.ageRange).match(/(\d+)\s*[–-]\s*(\d+)/);
	if (!m) return '';
	const min = Number(m[1]);
	const max = Number(m[2]);
	const n = parseInt(age, 10);
	if (Number.isNaN(n)) return '';
	if (n < min || n > max) {
		return `This session is for ages ${min}–${max}. Please choose the session that matches your child's age.`;
	}
	return '';
}

// Submit a workshop registration. Calls the createCheckoutSession Cloud
// Function, which validates capacity + holds a seat, then returns a Stripe
// Checkout URL to redirect to. Throws on validation / capacity / config errors.
export async function createWorkshopCheckout(payload) {
	const callable = httpsCallable(functions, 'createCheckoutSession');
	const res = await callable({ ...payload, origin: window.location.origin });
	return res.data; // { url, sessionId }
}

// Join a workshop's waitlist (shown only when a session is full). Calls the
// joinWorkshopWaitlist Cloud Function, which validates the session is actually
// full and stores the entry. Throws on validation / config errors.
export async function joinWorkshopWaitlist(payload) {
	const callable = httpsCallable(functions, 'joinWorkshopWaitlist');
	const res = await callable(payload);
	return res.data; // { ok, alreadyOnList }
}

// ── Admin (employee portal) reads ──

// All workshops regardless of status (for the management roster + capacity).
export async function fetchWorkshopsForAdmin() {
	const q = query(collection(db, 'workshops'), orderBy('sortOrder'));
	const snap = await getDocs(q);
	return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// All confirmed/refunded workshop enrollments. Pack enrollments share the
// `enrollments` collection (type:'pack') but belong to the lesson-pack roster,
// so they're excluded here. (Legacy workshop enrollments have no `type`.)
export async function fetchWorkshopEnrollments() {
	const snap = await getDocs(collection(db, 'enrollments'));
	return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((e) => e.type !== 'pack');
}

// Workshop-registration leads (pending / abandoned / converted intake records).
export async function fetchWorkshopLeads() {
	const q = query(collection(db, 'leads'), where('type', '==', 'workshop-registration'));
	const snap = await getDocs(q);
	return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Workshop waitlist entries (families waiting on a full session).
export async function fetchWorkshopWaitlist() {
	const snap = await getDocs(collection(db, 'waitlists'));
	return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
